import type { LiveBarProfile, LiveBarStatus } from './index';
import type { VolumeBarSnapshot } from 'src/lib/kiasi/bars';

export interface ReconnectPolicy {
    readonly initialDelayMs: number;
    readonly maxDelayMs: number;
    /** Consecutive sessions that ended without delivering a bar before the subscription gives up. */
    readonly maxAttempts: number;
}

export const DEFAULT_RECONNECT_POLICY: ReconnectPolicy = { initialDelayMs: 1_000, maxDelayMs: 30_000, maxAttempts: 5 };

/**
 * One subscription's connection lifecycle. The owner performs the effects
 * (opening a connection, arming a timer); this state decides what they mean.
 */
export interface LiveBarConnectionState {
    readonly status: LiveBarStatus;
    readonly statusMessage: string | null;
    /** Consecutive sessions that ended before delivering a bar; reset by the first commit. */
    readonly failures: number;
    /** Delay before the pending reconnect, set when `status` is `reconnecting`. */
    readonly reconnectDelayMs: number;
    readonly bar: VolumeBarSnapshot | null;
    /** The open session's delivery profile; each session selects its own. */
    readonly profile: LiveBarProfile | null;
    readonly closedRevision: number;
}

export function createInitialLiveBarConnectionState(): LiveBarConnectionState {
    return {
        status: 'unauthenticated',
        statusMessage: null,
        failures: 0,
        reconnectDelayMs: 0,
        bar: null,
        profile: null,
        closedRevision: 0,
    };
}

export type ConnectStartedAction = { type: 'CONNECT_STARTED' };
export const connectStarted = (): ConnectStartedAction => ({ type: 'CONNECT_STARTED' });

export type TransportStatusAction = { type: 'TRANSPORT_STATUS'; message: string; streaming: boolean };
export const transportStatus = (message: string, streaming: boolean): TransportStatusAction => ({
    type: 'TRANSPORT_STATUS',
    message,
    streaming,
});

export type ProfileSelectedAction = { type: 'PROFILE_SELECTED'; profile: LiveBarProfile | null };
export const profileSelected = (profile: LiveBarProfile | null): ProfileSelectedAction => ({
    type: 'PROFILE_SELECTED',
    profile,
});

/** A state commit; `final` for a close or correction, which history now holds. */
export type BarCommittedAction = { type: 'BAR_COMMITTED'; bar: VolumeBarSnapshot; final: boolean };
export const barCommitted = (bar: VolumeBarSnapshot, final: boolean): BarCommittedAction => ({
    type: 'BAR_COMMITTED',
    bar,
    final,
});

/** The stream withdrew its state; `removed` when history no longer holds the bar. */
export type BarWithdrawnAction = { type: 'BAR_WITHDRAWN'; removed: boolean };
export const barWithdrawn = (removed: boolean): BarWithdrawnAction => ({ type: 'BAR_WITHDRAWN', removed });

export type LateFrameAction = { type: 'LATE_FRAME' };
export const lateFrame = (): LateFrameAction => ({ type: 'LATE_FRAME' });

export type SessionEndedAction = { type: 'SESSION_ENDED'; reason: string; policy: ReconnectPolicy };
export const sessionEnded = (reason: string, policy: ReconnectPolicy): SessionEndedAction => ({
    type: 'SESSION_ENDED',
    reason,
    policy,
});

export type TokenRequiredAction = { type: 'TOKEN_REQUIRED' };
export const tokenRequired = (): TokenRequiredAction => ({ type: 'TOKEN_REQUIRED' });

export type DisposedAction = { type: 'DISPOSED' };
export const disposed = (): DisposedAction => ({ type: 'DISPOSED' });

export type LiveBarConnectionAction =
    | ConnectStartedAction
    | TransportStatusAction
    | ProfileSelectedAction
    | BarCommittedAction
    | BarWithdrawnAction
    | LateFrameAction
    | SessionEndedAction
    | TokenRequiredAction
    | DisposedAction;

export function liveBarConnectionReducer(
    state: LiveBarConnectionState,
    action: LiveBarConnectionAction,
): LiveBarConnectionState {
    if (state.status === 'disposed') return state;
    switch (action.type) {
        case 'CONNECT_STARTED': {
            // A fresh token after giving up earns a fresh retry budget. Whatever bar an earlier
            // session showed is unknown now, and so is its profile.
            const failures = state.status === 'error' ? 0 : state.failures;
            return {
                ...state,
                status: 'connecting',
                statusMessage: null,
                failures,
                reconnectDelayMs: 0,
                bar: null,
                profile: null,
            };
        }
        case 'TRANSPORT_STATUS': {
            const status = action.streaming && state.status === 'connecting' ? 'streaming' : state.status;
            if (status === state.status && state.statusMessage === action.message) return state;
            return { ...state, status, statusMessage: action.message };
        }
        case 'PROFILE_SELECTED': {
            const { profile } = action;
            if (profile?.name === state.profile?.name && profile?.cadenceMillis === state.profile?.cadenceMillis) {
                return state;
            }
            return { ...state, profile };
        }
        case 'BAR_COMMITTED':
            return {
                ...state,
                bar: action.bar,
                failures: 0,
                closedRevision: action.final ? state.closedRevision + 1 : state.closedRevision,
            };
        case 'BAR_WITHDRAWN':
            if (state.bar === null && !action.removed) return state;
            return {
                ...state,
                bar: null,
                closedRevision: action.removed ? state.closedRevision + 1 : state.closedRevision,
            };
        case 'LATE_FRAME':
            return { ...state, closedRevision: state.closedRevision + 1 };
        case 'SESSION_ENDED': {
            // The open bar is unknown once the stream is gone; a stale copy would be counted twice
            // when history catches up with that minute.
            if (state.failures >= action.policy.maxAttempts) {
                return {
                    ...state,
                    status: 'error',
                    statusMessage: action.reason,
                    bar: null,
                    profile: null,
                    reconnectDelayMs: 0,
                };
            }
            return {
                ...state,
                status: 'reconnecting',
                statusMessage: action.reason,
                failures: state.failures + 1,
                reconnectDelayMs: Math.min(
                    action.policy.maxDelayMs,
                    action.policy.initialDelayMs * 2 ** state.failures,
                ),
                bar: null,
                profile: null,
            };
        }
        case 'TOKEN_REQUIRED':
            if (state.status === 'unauthenticated') return state;
            return { ...state, status: 'unauthenticated', reconnectDelayMs: 0, bar: null, profile: null };
        case 'DISPOSED':
            return { ...state, status: 'disposed', reconnectDelayMs: 0, bar: null, profile: null };
        default: {
            const _exhaustiveCheck: never = action;
            return state;
        }
    }
}
